import { Box, Button, Chip, FormControl, Grid, IconButton, Modal, TextField, Typography } from '@mui/material';
import styles from './NewEmployeesModal.module.css';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { EmployeesService } from 'src/pehra-ts-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import TextFieldArray from 'src/components/TextFieldArray';
import { LoadingButton } from '@mui/lab';
import useNotification from 'src/hooks/useNotification';

export interface INewEmployeesModal {
  openModal: boolean;
  handleCloseModal: () => void;
  data?: any;
  editMode?: boolean;
}

const FIELD_LIST = [
  {
    label: 'Name',
    name: 'name',
    type: 'string',
    required: true
  },
  {
    label: 'Badge Number',
    name: 'badge_number',
    type: 'string',
    required: false
  },
  {
    label: 'Rank',
    name: 'rank',
    type: 'string',
    required: false
  },
  {
    label: 'PNO Number',
    name: 'pno_number',
    type: 'string',
    required: false
  },
  {
    label: 'EHRMS Code',
    name: 'ehrms_code',
    type: 'string',
    required: false
  },
  {
    label: "Father's Name",
    name: 'father_name',
    type: 'string',
    required: false
  },
  {
    label: 'Date of Birth',
    name: 'date_of_birth',
    type: 'date',
    required: false
  },
  {
    label: 'Mobile',
    name: 'mobile',
    type: 'string',
    required: false
  },
  {
    label: 'Rank Start Date',
    name: 'rank_start_date',
    type: 'date',
    required: false
  },
  {
    label: 'Employee Type',
    name: 'employee_type',
    type: 'string',
    required: false
  },
  {
    label: 'Date of Appointment',
    name: 'date_of_appointment',
    type: 'date',
    required: false
  },
  {
    label: 'Type of Appointment',
    name: 'type_of_appointment',
    type: 'string',
    required: false
  },
  {
    label: 'Gender',
    name: 'gender',
    type: 'string',
    required: false
  },
  {
    label: 'Caste',
    name: 'caste',
    type: 'string',
    required: false
  },
  {
    label: 'Category',
    name: 'category',
    type: 'string',
    required: false
  },
  {
    label: 'Education',
    name: 'education',
    type: 'string',
    required: false
  },
  {
    label: 'Home District',
    name: 'home_district',
    type: 'string',
    required: false
  },
  {
    label: 'Current Position',
    name: 'current_position',
    type: 'string',
    required: false
  },
  {
    label: 'Date of Current Position',
    name: 'date_of_current_position',
    type: 'date',
    required: false
  },
  {
    label: 'Date of Agra Posting',
    name: 'date_of_agra_posting',
    type: 'date',
    required: false
  },
  {
    label: 'Current Posting Role',
    name: 'current_posting_role',
    type: 'string',
    required: false
  },
  {
    label: 'Date of Posting to Zone',
    name: 'date_of_posting_to_zone',
    type: 'date',
    required: false
  },
  {
    label: 'Previous Postings',
    name: 'previous_postings',
    type: 'string',
    required: false
  },
  {
    label: 'Punishments',
    name: 'punishments',
    type: 'string',
    required: false
  },
  {
    label: 'Other Details',
    name: 'other_details',
    type: 'string',
    required: false
  }
];

const NewEmployeesModal: React.FC<INewEmployeesModal> = ({ openModal, handleCloseModal, data, editMode }) => {
  const [formData, setFormData] = useState<any>({});

  const queryClient = useQueryClient();

  const [sendNotification] = useNotification();

  const addEmployee = async () => {
    const body = {
      ...formData
    };

    try {
      const res = await EmployeesService.registerEmployees({
        employees: [body]
      });
      return res;
    } catch (error) {
      throw error;
    }
  };

  const updateEmployee = async () => {
    const body = {
      ...formData
    };

    try {
      const res = await EmployeesService.updateEmployee(data.id, body);
      return res;
    } catch (error) {
      throw error;
    }
  };

  const addEmployeeMutation = useMutation({
    mutationFn: addEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['employeesList']
      });
      sendNotification({
        msg: 'Employee added successfully',
        type: 'success'
      });
      handleCloseModal();
    }
  });

  const updateEmployeeMutation = useMutation({
    mutationFn: updateEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['employeesList']
      });
      sendNotification({
        msg: 'Employee updated successfully',
        type: 'success'
      });
      // handleCloseModal();
    }
  });

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveClick = (e: any) => {
    e.preventDefault();
    if (editMode) {
      updateEmployeeMutation.mutate();
    } else {
      addEmployeeMutation.mutate();
    }
  };

  useEffect(() => {
    if (data) {
      const { id, state, ...rest } = data;
      setFormData(rest);
    }
  }, [data]);

  const modalContent = (
    <form onSubmit={handleSaveClick}>
      <FormControl
        sx={{
          width: '100%'
        }}
      >
        <Grid
          container
          sx={{
            width: '100%'
          }}
        >
          {FIELD_LIST.map((field) => {
            return (
              <Grid key={field.name} item style={{ padding: '20px' }} xs={12} md={6}>
                <Typography variant="h6">{field.label}</Typography>
                {field.type === 'date' ? (
                  <TextField
                    className={styles.input}
                    type="date"
                    placeholder={`Enter ${field.name}`}
                    name={field.name}
                    onChange={handleFormChange}
                    required={field.required}
                    fullWidth
                    value={formData[field.name]}
                  />
                ) : (
                  <TextField
                    className={styles.input}
                    type="text"
                    placeholder={`Enter ${field.name}`}
                    name={field.name}
                    onChange={handleFormChange}
                    required={field.required}
                    fullWidth
                    value={formData[field.name]}
                  />
                )}
              </Grid>
            );
          })}
          <Grid item style={{ padding: '20px' }} xs={12} md={6}>
            <Typography variant="h6">Special Qualifications (Press Enter To Add)</Typography>
            <TextFieldArray
              handleFormData={(data) => setFormData({ ...formData, special_qualifications: data })}
              name="special_qualifications"
              placeholder="Enter Special Qualifications (Press Enter to add)"
            />
          </Grid>
        </Grid>
        <Box>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            style={{ margin: '20px' }}
            fullWidth
            loading={addEmployeeMutation.isPending || updateEmployeeMutation.isPending}
          >
            Save
          </LoadingButton>
        </Box>
      </FormControl>
    </form>
  );

  return (
    <Modal open={openModal} onClose={handleCloseModal}>
      <Box className={styles.modalContainer}>
        <IconButton onClick={handleCloseModal} style={{ position: 'absolute', right: '10px', top: '10px' }}>
          <CloseIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" style={{ padding: '20px' }}>
           {editMode ? "Edit Employee Details" : "Add New Employee"}
          </Typography>
        </Box>
        {modalContent}
      </Box>
    </Modal>
  );
};

export default NewEmployeesModal;
